export function preservePreview(target, current, view = 'gp-plants') {
  const result = new URL(target, current);
  const source = new URL(current);
  result.searchParams.set('view', view);
  for (const [key, value] of source.searchParams) {
    if (key.startsWith('utm_') || key === 'preview_theme_id') result.searchParams.set(key, value);
  }
  return result;
}

class PlantFilters extends HTMLElement {
  connectedCallback() {
    this.view = this.dataset.view || 'gp-plants';
    this.controller = new AbortController();
    const main = document.querySelector('.shopify-section[id$="__main-collection"]');
    if (main && !history.state?.FacetUrl) {
      history.replaceState({ ...history.state, FacetUrl: window.location.href, section_id: main.id.replace('shopify-section-', '') }, '', window.location.href);
    }
    const options = { signal: this.controller.signal };
    this.addEventListener('click', (event) => {
      const link = event.target.closest('.gp-plants__card, .gp-plants__clear, .gp-plants__sticky-link');
      if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (link.getAttribute('aria-disabled') === 'true') {
        event.preventDefault();
        return;
      }
      const main = document.querySelector('.shopify-section[id$="__main-collection"]');
      if (!main?.querySelector('[main-content]') || !customElements.get('hdt-facet-filters-form')) return;
      event.preventDefault();
      const url = preservePreview(link.href, window.location.href, this.view);
      url.searchParams.set('section_id', main.id.replace('shopify-section-', ''));
      document.dispatchEvent(new CustomEvent('facet:render', {
        detail: { url, disableScrollToVIew: true }
      }));
    }, options);
    document.addEventListener('facet:render', (event) => {
      if (event.detail?.url) {
        event.detail.url.searchParams.set('view', this.view);
        this.pendingFacet = true;
      }
    }, { ...options, capture: true });
    document.addEventListener('theme:loading:done', () => {
      if (!this.pendingFacet) return;
      this.pendingFacet = false;
      clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => this.refresh(), 0);
    }, options);
    window.addEventListener('popstate', (event) => {
      if (!event.state?.FacetUrl) this.refresh();
    }, options);
    const scheduleSticky = () => {
      if (this.stickyFrame) return;
      this.stickyFrame = requestAnimationFrame(() => {
        this.stickyFrame = 0;
        this.updateSticky();
      });
    };
    window.addEventListener('scroll', scheduleSticky, { ...options, passive: true });
    window.addEventListener('resize', scheduleSticky, options);
    this.stickyObserver = new ResizeObserver(scheduleSticky);
    this.stickyObserver.observe(this);
    if (main) this.stickyObserver.observe(main);
    const header = document.querySelector('.hdt-section-header');
    if (header) this.stickyObserver.observe(header);
    this.prepare();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.refreshRequest?.abort();
    this.priceRequests?.forEach((request) => request.abort());
    clearTimeout(this.refreshTimer);
    cancelAnimationFrame(this.stickyFrame);
    this.stickyObserver?.disconnect();
  }

  prepare() {
    for (const link of this.querySelectorAll('.gp-plants__card, .gp-plants__clear')) {
      link.href = preservePreview(link.href, window.location.href, this.view).toString();
    }
    for (const link of document.querySelectorAll('.hdt-pagination a[href]')) {
      link.href = preservePreview(link.href, window.location.href, this.view).toString();
    }
    this.prepareSticky();
    this.updateSticky();
    this.updateBudget();
  }

  prepareSticky() {
    if (this.dataset.mode === 'catalog') return;
    if (this.querySelector('.gp-plants__sticky')) return;
    const grid = this.querySelector('.gp-plants__grid');
    if (!grid) return;
    const bar = document.createElement('nav');
    bar.className = 'gp-plants__sticky';
    bar.setAttribute('aria-label', grid.getAttribute('aria-label'));
    bar.hidden = true;
    const track = document.createElement('div');
    track.className = 'gp-plants__sticky-track';
    for (const source of this.querySelectorAll('.gp-plants__card, .gp-plants__clear')) {
      const link = source.cloneNode(false);
      link.removeAttribute('id');
      link.removeAttribute('data-shopify-editor-block');
      link.className = 'gp-plants__sticky-link';
      link.dataset.kind = source.dataset.kind || 'clear';
      link.textContent = source.querySelector('.gp-plants__label')?.textContent || source.textContent;
      track.append(link);
    }
    bar.append(track);
    this.append(bar);
  }

  updateSticky() {
    if (this.dataset.mode === 'catalog') return;
    const bar = this.querySelector('.gp-plants__sticky');
    const grid = this.querySelector('.gp-plants__grid');
    const main = document.querySelector('.shopify-section[id$="__main-collection"]');
    if (!bar || !grid || !main) return;
    const header = document.querySelector('.hdt-section-header');
    const top = Math.max(0, header?.getBoundingClientRect().bottom || 0);
    bar.style.top = `${top}px`;
    // Keep the full image grid at the top; the text strip serves the product list only.
    bar.hidden = grid.getBoundingClientRect().bottom > top || main.getBoundingClientRect().bottom <= top + 54;
  }

  async refresh() {
    this.refreshRequest?.abort();
    const request = this.refreshRequest = new AbortController();
    const url = preservePreview(window.location.href, window.location.href, this.view);
    url.searchParams.delete('sections');
    url.searchParams.set('section_id', this.dataset.sectionId);
    try {
      const response = await fetch(url, { signal: request.signal });
      if (!response.ok) return;
      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = page.querySelector('gp-plants-filters');
      if (!fresh || request.signal.aborted) return;
      const focused = document.activeElement?.closest('.gp-plants__card, .gp-plants__clear, .gp-plants__sticky-link');
      const focusedKind = focused && this.contains(focused) ? focused.dataset.kind || 'clear' : null;
      const focusedSticky = focused?.classList.contains('gp-plants__sticky-link');
      const stickyScroll = this.querySelector('.gp-plants__sticky-track')?.scrollLeft || 0;
      this.innerHTML = fresh.innerHTML;
      this.dataset.resultCount = fresh.dataset.resultCount;
      this.dataset.activeCount = fresh.dataset.activeCount;
      this.prepare();
      const track = this.querySelector('.gp-plants__sticky-track');
      if (track) track.scrollLeft = stickyScroll;
      if (focusedKind) {
        const target = focusedSticky
          ? this.querySelector(`.gp-plants__sticky-link[data-kind="${focusedKind}"]`) || this.querySelector('.gp-plants__sticky-link')
          : focusedKind === 'clear' ? this.querySelector('.gp-plants__clear') || this.querySelector('.gp-plants__card') : this.querySelector(`.gp-plants__card[data-kind="${focusedKind}"]`);
        target?.focus({ preventScroll: true });
      }
    } catch (error) {
      if (error.name !== 'AbortError') this.prepare();
    }
  }

  updateBudget() {
    this.priceRequests?.forEach((request) => request.abort());
    this.priceRequests = [];
    for (const card of this.querySelectorAll('[data-price-card="true"]')) {
      if (card.dataset.active !== 'true') this.updatePriceCard(card);
    }
  }

  async updatePriceCard(card) {
    const request = new AbortController();
    this.priceRequests.push(request);
    const url = new URL(card.href);
    url.searchParams.set('section_id', this.dataset.sectionId);
    try {
      const response = await fetch(url, { signal: request.signal });
      if (!response.ok) return;
      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      const result = page.querySelector('gp-plants-filters');
      if (!result || request.signal.aborted || !card.isConnected) return;
      const count = Number(result.dataset.resultCount);
      if (!Number.isFinite(count)) return;
      const label = card.querySelector('.gp-plants__count');
      label.textContent = label.dataset.countFormat.replace('__COUNT__', String(count));
      label.hidden = false;
      if (this.dataset.mode === 'catalog' && card.dataset.active !== 'true') {
        card.hidden = count === 0 || (Number(this.dataset.activeCount) === 0 && count >= Number(this.dataset.resultCount));
      }
      if (count === 0) {
        card.setAttribute('aria-disabled', 'true');
        card.setAttribute('tabindex', '-1');
      } else {
        card.removeAttribute('aria-disabled');
        card.removeAttribute('tabindex');
      }
      const sticky = this.querySelector(`.gp-plants__sticky-link[data-kind="${card.dataset.kind}"]`);
      if (sticky) {
        for (const attr of ['aria-disabled', 'tabindex']) {
          if (card.hasAttribute(attr)) sticky.setAttribute(attr, card.getAttribute(attr));
          else sticky.removeAttribute(attr);
        }
      }
    } catch (error) {
      if (error.name !== 'AbortError' && card.isConnected) card.querySelector('.gp-plants__count').hidden = true;
    }
  }
}

if (!customElements.get('gp-plants-filters')) customElements.define('gp-plants-filters', PlantFilters);

